import { createSlice } from '@reduxjs/toolkit';
import { compareObjectByKey, getDefaultSortKey } from '../helpers/DataHelpers';
import * as RhinoformService from '../services/RhinoformService';
import NotificationService from '../services/NotificationService';
import { receiveUserForms } from './userReducer';
import { FORMS_LIST_HEADERS_CONFIG } from '../constants/LibraryConstants';
import { JOTFORM_COOKIES } from '../constants/AppConstants';

// SLICE
const paySlice = createSlice({
  name: 'FORM',
  initialState: {
    org: {},
    forms: [],
    allForms: [],
    count: 0,
    pageLoading: false,
    isOrgFormInProgress: false,
    isOrgLoading: false,
    isFormTemplateDeleting: false,
    form: {},
    isFormLoading: false,
    isFormTemplateUpdating: false,
    formS3Url: {},
    isPdfLoading: false,
    isSaveDraftFormLoading: false,
    isSaveFormLoading: false,
    contactForms: {},
    isContactFormLoading: false,
    otherUserForms: {},
    formUploadPostProcessing: [],
  },
  reducers: {
    receiveFormOrganization: (state, action) => ({
      ...state,
      org: {
        ...state.org,
        ...action.payload.org,
      },
      isOrgLoading: action.payload.isOrgLoading,
      isOrgFormInProgress: action.payload.isOrgFormInProgress,
    }),
    receiveFormUrl: (state, action) => ({
      ...state,
      form: {
        ...state.form,
        ...action.payload.form,
      },
      isFormLoading: action.payload.isFormLoading,
      isSaveDraftFormLoading: false,
      isSaveFormLoading: false,
      pageLoading: false,
    }),
    removeForm: state => ({
      ...state,
      form: {},
      isFormLoading: false,
    }),
    requestFormOrganization: state => ({
      ...state,
      isOrgLoading: true,
    }),
    setFormOrganizationInProgress: state => ({
      ...state,
      isFormInProgress: true,
    }),
    setFormInProgress: state => ({
      ...state,
      isFormLoading: true,
      pageLoading: true,
    }),
    setFormUploadPostProcessingInProgress: (state, action) => ({
      ...state,
      isFormLoading: true,
      formUploadPostProcessing: [...state.formUploadPostProcessing, action.payload.key],
    }),
    receiveWebSocketFormUploadPostProcessing: (state, action) => ({
      ...state,
      isFormLoading: false,
      formUploadPostProcessing: state.formUploadPostProcessing.filter(key => key !== action.payload.key),
      form: {
        ...state.form,
        ...action.payload,
      },
      isSaveDraftFormLoading: false,
      isSaveFormLoading: false,
    }),
    receiveFormOrganizationError: state => ({
      ...state,
      org: {},
      isOrgFormInProgress: false,
      isOrgLoading: false,
    }),
    receiveFormUrlError: state => ({
      ...state,
      form: {},
      isFormLoading: false,
      isPdfLoading: false,
      pageLoading: false,
    }),
    receiveUpdatedFormTemplate: (state, action) => ({
      ...state,
      forms: updateFormTemplate(state, action),
      allForms: state.allForms.map((form) => {
        if (form.formId === action.payload.formId) {
          return {
            ...form,
            updatedAt: action.payload.updatedAt,
            lastModifiedBy: action.payload.keyToUpdate === 'isPublished' ? action.payload.lastModifiedBy : form.lastModifiedBy,
            [action.payload.keyToUpdate]: !form[action.payload.keyToUpdate],
          };
        }
        return form;
      }),
      count: action.payload.selectedFormFilter === 'favorite' && action.payload.keyToUpdate === 'isFavorite' ? state.count - 1 : state.count,
      isFormTemplateUpdating: false,
    }),
    receiveFormTemplates: (state, action) => {
      let allForms = action.payload.allForms ? [...action.payload.allForms] : state.allForms;
      let forms = [...action.payload.forms];
      if (action.payload.allForms) {
        const defaultSortKey = getDefaultSortKey(FORMS_LIST_HEADERS_CONFIG);
        const favoriteForms = action.payload.forms.filter(form => form.isFavorite);
        const nonFavoriteForms = action.payload.forms.filter(form => !form.isFavorite);
        const sortedFavorites = favoriteForms.sort((a, b) => compareObjectByKey(a, b, defaultSortKey));
        const sortedNonFavorites = nonFavoriteForms.sort((a, b) => compareObjectByKey(a, b, defaultSortKey));
        forms = [...sortedFavorites, ...sortedNonFavorites];
        allForms = forms;
      }
      return {
        ...state,
        forms,
        allForms,
        count: action.payload.forms.length,
        pageLoading: false,
      };
    },
    requestFormTemplates: state => ({
      ...state,
      pageLoading: true,
    }),
    receiveFormTemplatesError: state => ({
      ...state,
      forms: [],
      count: 0,
      pageLoading: false,
    }),
    receiveUpdatedFormsList: (state, action) => ({
      ...state,
      forms: action.payload.forms,
      count: action.payload.count,
      pageLoading: action.payload.pageLoading,
    }),
    requestDeleteFormTemplate: state => ({
      ...state,
      isFormTemplateDeleting: true,
    }),
    requestUpdateFormTemplate: state => ({
      ...state,
      isFormTemplateUpdating: true,
    }),
    removeFormTemplate: (state, action) => ({
      ...state,
      forms: deleteFormTemplatePayload(state, action),
      allForms: state.allForms.filter(form => form.formId !== action.payload),
      count: state.count - 1,
      isFormTemplateDeleting: false,
    }),
    clearFormTemplates: state => ({
      ...state,
      forms: [],
      count: 0,
      pageLoading: true,
    }),
    receiveFormS3Url: (state, action) => ({
      ...state,
      formS3Url: {
        ...state.formS3Url,
        [action.payload._id]: action.payload,
        isPdfLoading: false,
      },
      otherUserForms: {
        [action.payload._id]: action.payload,
      },
    }),
    setPdfLoadingInProgress: state => ({
      ...state,
      formS3Url: {
        ...state.formS3Url,
        isPdfLoading: true,
      },
    }),
    setFormLoadingInProgress: (state, action) => ({
      ...state,
      isFormLoading: true,
      isSaveFormLoading: action.payload,
      isSaveDraftFormLoading: !action.payload,
    }),
    receiveContactForms: (state, action) => ({
      ...state,
      contactForms: {
        result: action.payload.forms,
        total: action.payload.count,
      },
      isContactFormLoading: false,
    }),
    setContactFormLoadingInProgress: state => ({
      ...state,
      isContactFormLoading: true,
    }),
  },
});

export default paySlice.reducer;

// ACTIONS
export const {
  receiveFormOrganization,
  requestFormOrganization,
  setFormOrganizationInProgress,
  receiveFormOrganizationError,
  receiveFormTemplates,
  requestFormTemplates,
  receiveFormTemplatesError,
  receiveUpdatedFormTemplate,
  receiveUpdatedFormsList,
  requestDeleteFormTemplate,
  clearFormTemplates,
  removeFormTemplate,
  requestPageData,
  receiveFormUrl,
  receiveFormUrlError,
  removeForm,
  setFormInProgress,
  setPdfFormProcess,
  requestUpdateFormTemplate,
  receiveFormS3Url,
  receiveFormS3UrlError,
  setPdfLoadingInProgress,
  setFormLoadingInProgress,
  setContactFormLoadingInProgress,
  receiveContactForms,
  setFormUploadPostProcessingInProgress,
  receiveWebSocketFormUploadPostProcessing,
} = paySlice.actions;

export function fetchOrgConfiguration(orgId) {
  return (dispatch) => {
    dispatch(requestFormOrganization());
    return RhinoformService.getOrganization(orgId)
      .then((response) => {
        dispatch(receiveFormOrganization(prepareFormPayload(response)));
      })
      .catch((err) => {
        console.error(err); // eslint-disable-line no-console
        dispatch(receiveFormOrganizationError());
      });
  };
}

export function createOrgConfiguration(payload) {
  return (dispatch) => {
    dispatch(setFormOrganizationInProgress());
    return RhinoformService.postOrganization(payload)
      .then((response) => {
        dispatch(receiveFormOrganization(prepareFormPayload(response)));
        NotificationService('createFormOrganizationConfiguration', response);
      })
      .catch((err) => {
        console.error(err); // eslint-disable-line no-console
        dispatch(receiveFormOrganizationError());
        NotificationService('createFormOrganizationConfiguration', err.response);
      });
  };
}

export function updateOrgConfiguration(payload) {
  return (dispatch) => {
    dispatch(setFormOrganizationInProgress());
    return RhinoformService.patchOrganization(payload)
      .then((response) => {
        dispatch(receiveFormOrganization(prepareFormPayload(response)));
        NotificationService('updateFormOrganizationConfiguration', response);
      })
      .catch((err) => {
        console.error(err); // eslint-disable-line no-console
        dispatch(receiveFormOrganizationError());
        NotificationService('updateFormOrganizationConfiguration', err.response);
      });
  };
}

export function fetchFormTemplates(filter, isPublished = false) {
  return (dispatch) => {
    dispatch(requestFormTemplates());
    dispatch(clearFormTemplates());
    return RhinoformService.getFormTemplates(isPublished)
      .then((response) => {
        dispatch(receiveFormTemplates(prepareFormsTemplatePayload(response.data?.forms, filter)));
      })
      .catch((err) => {
        // eslint-disable-next-line no-console
        console.error(err);
        dispatch(receiveFormTemplatesError());
        NotificationService('fetchFormTemplates', err.response);
      });
  };
}

export function fetchContactForms(contactId) {
  return dispatch => RhinoformService.getContactForms(contactId)
    .then((response) => {
      const users = {
        [contactId]: {
          forms: response.data?.forms,
        },
      };
      dispatch(receiveUserForms({ users }));
    })
    .catch((err) => {
      // eslint-disable-next-line no-console
      console.error(err);
    });
}


export function updateFormsList(formsList) {
  return (dispatch) => {
    dispatch(clearFormTemplates());
    dispatch(receiveFormTemplates(prepareFormsTemplatePayload(formsList)));
  };
}

export function updateFormTemplates(formId, payload, selectedFormFilter = '') {
  return (dispatch) => {
    if (payload.isPublished) dispatch(requestUpdateFormTemplate());
    return RhinoformService.patchFormTemplates(formId, payload)
      .then((response) => {
        let responsePayload = {};
        if (payload.isPublished) {
          responsePayload = {
            ...response.data,
            selectedFormFilter,
            keyToUpdate: 'isPublished',
          };
          NotificationService('formTemplatePublished', response);
        } else {
          responsePayload = {
            ...response.data,
            selectedFormFilter,
            keyToUpdate: 'isFavorite',
          };
        }
        dispatch(receiveUpdatedFormTemplate(responsePayload));
      })
      .catch((err) => {
        // eslint-disable-next-line no-console
        console.error(err);
        if (payload.isPublished) {
          NotificationService('formTemplatePublished', err.response);
        } else {
          NotificationService('updateFormTemplate', err.response);
        }
      });
  };
}

export function deleteFormTemplate(formId, selectedFormFilter) {
  return (dispatch) => {
    dispatch(requestDeleteFormTemplate());
    return RhinoformService.deleteFormTemplate(formId)
      .then((response) => {
        dispatch(removeFormTemplate(formId, selectedFormFilter));
        NotificationService('deleteFormTemplate', response);
      })
      .catch((err) => {
        // eslint-disable-next-line no-console
        console.error(err);
        NotificationService('deleteFormTemplate', err.response);
      });
  };
}

export function prepareFormPayload(Org) {
  return {
    org: {
      isRhinoformEnabled: !!Org.data?.isEnabled,
      username: Org.data?.username,
      seatsLimit: Org.data?.memberSeats,
      email: Org.data?.email,
    },
    isOrgLoading: false,
    isOrgFormInProgress: false,
  };
}

export function shapeFormUrlPayload(form) {
  return {
    form: {
      iFrameUrl: form.data?.iFrameUrl,
      formExternalId: form.data?.formExternalId,
      title: form.data?.title,
      formId: form.data?.formId,
      formUrl: form.data?.formUrl,
      version: form.data?.version,
      isPdf: form.data?.isPdf,
      favorites: form.data?.favorites,
      pdfFileName: form.data?.pdfFileName,
      sourceId: form.data?.sourceId,
    },
    isFormLoading: false,
  };
}

export function getAuthenticateOrgUser(payload, type, formData, config, isFormEditableMode) {
  return (dispatch) => {
    dispatch(setFormInProgress());
    return RhinoformService.getAuthenticatedUser()
      .then((response) => {
        if (!process.env.CIRCLE_BRANCH) {
          removeJotformCookies();
        }
        response.data.forEach((cokie) => {
          document.cookie = cokie;
        });
      })
      .then(() => {
        if (isFormEditableMode) {
          return dispatch(cloneRhinoform(payload));
        } else {
          return this.buildRhinoform(payload, type, formData, config);
        }
      })
      .catch((err) => {
        dispatch(receiveFormUrlError());
        console.error(err); // eslint-disable-line no-console
        dispatch(receiveFormUrlError());
        NotificationService('rhinoformCreated', err.response);
      });
  };
}

// RHIN-6885 sometime jotform cookies are messed and page not authenticated so clearing cookies everytime before adding.
function removeJotformCookies() {
  const url = process.env.REACT_APP_URL;
  const domain = url.substr(url.indexOf('.'));
  const cookies = document.cookie.split(';');
  for (let i = 0; i < cookies.length; i++) {
    const key = cookies[i].split('=')[0].trim();
    if (JOTFORM_COOKIES.includes(key)) {
      document.cookie = `${key}=; expires=${new Date(0).toUTCString()}; domain=${domain}; path=/`;
    }
  }
}

export function deleteRhinoform(payload) {
  return dispatch =>
    RhinoformService.deleteRhinoform(payload)
      .then((response) => {
        dispatch(removeForm(response));
      })
      .catch((err) => {
        console.error(err); // eslint-disable-line no-console
      });
}

export function createRhinoForm(payload) {
  return (dispatch) => {
    const notificationType = payload.isEditMode ? 'rhinoFormUpdated' : 'rhinoformCreated';
    dispatch(setFormLoadingInProgress(payload.isPublished));
    return RhinoformService.createRhinoform(payload)
      .then((response) => {
        dispatch(receiveFormUrl(shapeFormUrlPayload(response)));
        NotificationService(notificationType, response);
      })
      .catch((err) => {
        console.error(err); // eslint-disable-line no-console
        dispatch(receiveFormUrlError());
        NotificationService('rhinoformCreated', err.response);
      });
  };
}

export function buildRhinoform(payload, type, formData, config, isUpdate) {
  return (dispatch) => {
    dispatch(setFormInProgress());
    return RhinoformService.buildFormTemplate(payload, type, formData, config, isUpdate)
      .then((response) => {
        dispatch(receiveFormUrl(shapeFormUrlPayload(response)));
      })
      .catch((err) => {
        console.error(err); // eslint-disable-line no-console
        dispatch(receiveFormUrlError());
        NotificationService('rhinoformCreated', err.response);
      });
  };
}

export function uploadPresignPDF(payload) {
  return (dispatch) => {
    dispatch(setFormUploadPostProcessingInProgress(payload));
    return RhinoformService.postPresignPDF(payload)
      .catch((err) => {
        console.error(err); // eslint-disable-line no-console
        dispatch(receiveFormUrlError());
        NotificationService('rhinoformCreated', err.response);
      });
  };
}

export function prepareFormsTemplatePayload(Forms, filter) {
  const formsPayload = {
    forms: [...Forms],
  };

  if (filter === 'all') {
    formsPayload.allForms = [...Forms];
  }
  return formsPayload;
}

export function prepareUpdateFormsListPayload(formsList) {
  return {
    forms: formsList,
    count: formsList?.length,
    pageLoading: false,
  };
}

function deleteFormTemplatePayload(state, action) {
  const forms = [...state.allForms];
  const filteredForm = forms.filter(form => form.formId !== action.payload);
  return filteredForm;
}

function updateFormTemplate(state, action) {
  const { selectedFormFilter, keyToUpdate, ...formPayload } = action.payload;
  const forms = [...state.forms];
  let updatedForms = [];
  if (selectedFormFilter === 'all') {
    updatedForms = forms.map((form) => {
      if (form.formId === formPayload.formId) {
        return {
          ...formPayload,
          lastModifiedBy: form.lastModifiedBy,
        };
      }
      return form;
    });
  } else if (selectedFormFilter === 'favorite' && keyToUpdate === 'isFavorite') {
    updatedForms = forms.filter(form => form.formId !== formPayload.formId);
  }
  return updatedForms;
}

export function cloneRhinoform(payload) {
  return (dispatch) => {
    dispatch(setFormInProgress());
    // dispatch(removeForm());
    return RhinoformService.cloneFormTemplate(payload.formId, payload)
      .then((response) => {
        dispatch(receiveFormUrl(shapeFormUrlPayload(response)));
      })
      .catch((err) => {
        console.error(err); // eslint-disable-line no-console
        dispatch(receiveFormUrlError());
        NotificationService('rhinoFormUpdated', err.response);
      });
  };
}

export function getFormTemplateById(formId) {
  return (dispatch) => {
    dispatch(setPdfLoadingInProgress());
    return RhinoformService.getFormTemplateById(formId)
      .then((response) => {
        dispatch(receiveFormS3Url(response.data));
      })
      .catch((err) => {
        console.error(err); // eslint-disable-line no-console
        dispatch(receiveFormUrlError());
      });
  };
}

export function getFormById(formId) {
  return dispatch => RhinoformService.getFormById(formId)
    .then((response) => {
      dispatch(receiveFormS3Url(response.data));
    })
    .catch((err) => {
      console.error(err); // eslint-disable-line no-console
      dispatch(receiveFormUrlError());
    });
}

export function getContactForms(params) {
  return (dispatch) => {
    dispatch(setContactFormLoadingInProgress());
    return RhinoformService.getPatientForms(params)
      .then((response) => {
        dispatch(receiveContactForms(response.data));
      })
      .catch((err) => {
        console.error(err); // eslint-disable-line no-console
        dispatch(receiveFormUrlError());
      });
  };
}
